import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Button,
  Flex,
  Box,
  Text,
  Checkbox,
  VStack,
  Select,
  Badge,
  Divider,
  InputGroup,
  InputLeftElement,
  Tooltip,
  Grid,
  GridItem,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { Folder, Buildings, Flag, Palette } from 'phosphor-react';
import { IKanbanProject, IKanbanColumn } from '../../hooks/useKanban';
import { useDepartamentos, IDepartamento } from '../../hooks/useDepartamentos';
import { IDataNoPagination } from '../../types/shared';

const COLOR_PALETTE = [
  '#6366F1',
  '#8B5CF6',
  '#A855F7',
  '#EC4899',
  '#F43F5E',
  '#EF4444',
  '#F97316',
  '#EAB308',
  '#84CC16',
  '#22C55E',
  '#14B8A6',
  '#06B6D4',
  '#3B82F6',
  '#2563EB',
  '#7C3AED',
  '#64748B',
];

interface KanbanProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  project: IKanbanProject | null;
  columns?: IKanbanColumn[];
  onSave: (
    // eslint-disable-next-line no-unused-vars
    nome: string,
    // eslint-disable-next-line no-unused-vars
    descricao: string,
    // eslint-disable-next-line no-unused-vars
    departamentoIds: number[],
    // eslint-disable-next-line no-unused-vars
    projectId?: number,
    // eslint-disable-next-line no-unused-vars
    colunaFinalizadoId?: number | null,
    // eslint-disable-next-line no-unused-vars
    cor?: string | null,
  ) => void;
}

export const KanbanProjectModal: React.FC<KanbanProjectModalProps> = ({
  isOpen,
  onClose,
  project,
  columns,
  onSave,
}) => {
  const [nome, setNome] = useState('');
  const [descricao, setDescricao] = useState('');
  const [cor, setCor] = useState('');
  const [selectedDepartamentos, setSelectedDepartamentos] = useState<number[]>([]);
  const [colunaFinalizadoId, setColunaFinalizadoId] = useState<number | null>(null);
  const { getDepartamentos } = useDepartamentos();

  const { data: departamentosData } = useQuery<IDataNoPagination<IDepartamento[]>>({
    queryKey: ['departamentos-kanban-project'],
    queryFn: getDepartamentos,
    enabled: isOpen,
  });

  const departamentos = departamentosData?.data || [];

  useEffect(() => {
    if (project) {
      setNome(project.nome || project.titulo || '');
      setDescricao(project.descricao || '');
      setCor(project.cor || '');
      setSelectedDepartamentos(project.departamentos?.map(d => d.id) || []);
      setColunaFinalizadoId(project.coluna_finalizado_id || null);
    } else {
      setNome('');
      setDescricao('');
      setCor('');
      setSelectedDepartamentos([]);
      setColunaFinalizadoId(null);
    }
  }, [project, isOpen]);

  const handleSave = () => {
    if (nome.trim()) {
      onSave(
        nome.trim(),
        descricao,
        selectedDepartamentos,
        project?.id,
        colunaFinalizadoId,
        cor || null,
      );
      onClose();
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && nome.trim()) {
      handleSave();
    }
  };

  const toggleDepartamento = (depId: number) => {
    if (selectedDepartamentos.includes(depId)) {
      setSelectedDepartamentos(selectedDepartamentos.filter(id => id !== depId));
    } else {
      setSelectedDepartamentos([...selectedDepartamentos, depId]);
    }
  };

  const isValidHex = (hex: string) => /^#[0-9A-Fa-f]{6}$/.test(hex);

  const handleHexChange = (value: string) => {
    let hex = value;
    if (hex && !hex.startsWith('#')) hex = `#${hex}`;
    if (hex.length <= 7) setCor(hex);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="3xl" isCentered>
      <ModalOverlay bg="blackAlpha.400" />
      <ModalContent borderRadius="2xl" mx={4} overflow="hidden">
        {/* Header */}
        <Box bg="gray.50" borderBottom="1px solid" borderColor="gray.100">
          <ModalHeader pb={3} pt={5} px={6}>
            <Flex alignItems="center" gap={3}>
              <Flex
                w="36px"
                h="36px"
                borderRadius="10px"
                bg="white"
                border="1px solid"
                borderColor="gray.200"
                align="center"
                justify="center"
                flexShrink={0}
              >
                <Folder size={18} color="#6366F1" weight="duotone" />
              </Flex>
              <Box>
                <Text fontSize="md" fontWeight="600" color="gray.800">
                  {project ? 'Editar projeto' : 'Novo projeto'}
                </Text>
                <Text fontSize="11px" color="gray.400" fontWeight="400">
                  {project
                    ? 'Atualize as informacoes do projeto'
                    : 'Crie um novo quadro para organizar tarefas'}
                </Text>
              </Box>
            </Flex>
          </ModalHeader>
          <ModalCloseButton top={5} right={5} borderRadius="full" />
        </Box>

        <ModalBody py={5} px={6}>
          {/* Layout horizontal: 2 colunas */}
          <Flex gap={6} direction={{ base: 'column', md: 'row' }}>
            {/* Coluna esquerda: dados basicos */}
            <Box flex={1} minW={0}>
              <FormControl mb={4}>
                <FormLabel
                  fontSize="12px"
                  fontWeight="600"
                  color="gray.500"
                  mb={1.5}
                  textTransform="uppercase"
                  letterSpacing="0.05em"
                >
                  Nome
                </FormLabel>
                <Input
                  value={nome}
                  onChange={e => setNome(e.target.value)}
                  onKeyDown={handleKeyPress}
                  placeholder="Nome do projeto..."
                  size="md"
                  borderRadius="xl"
                  bg="white"
                  border="1px solid"
                  borderColor="gray.200"
                  fontSize="14px"
                  _focus={{
                    borderColor: 'blue.400',
                    boxShadow: '0 0 0 3px rgba(66, 153, 225, 0.1)',
                  }}
                  _placeholder={{ color: 'gray.300' }}
                  autoFocus
                />
              </FormControl>

              <FormControl mb={4}>
                <FormLabel
                  fontSize="12px"
                  fontWeight="600"
                  color="gray.500"
                  mb={1.5}
                  textTransform="uppercase"
                  letterSpacing="0.05em"
                >
                  Descricao
                </FormLabel>
                <Textarea
                  value={descricao}
                  onChange={e => setDescricao(e.target.value)}
                  placeholder="Descreva o objetivo do projeto..."
                  borderRadius="xl"
                  minH="70px"
                  bg="white"
                  border="1px solid"
                  borderColor="gray.200"
                  fontSize="14px"
                  resize="vertical"
                  _focus={{
                    borderColor: 'blue.400',
                    boxShadow: '0 0 0 3px rgba(66, 153, 225, 0.1)',
                  }}
                  _placeholder={{ color: 'gray.300' }}
                />
              </FormControl>

              {/* Cor do projeto */}
              <FormControl>
                <Flex alignItems="center" gap={1.5} mb={2}>
                  <Palette size={14} color="#718096" weight="bold" />
                  <FormLabel
                    fontSize="12px"
                    fontWeight="600"
                    color="gray.500"
                    mb={0}
                    textTransform="uppercase"
                    letterSpacing="0.05em"
                  >
                    Cor do projeto
                  </FormLabel>
                </Flex>

                {/* Paleta de cores */}
                <Grid templateColumns="repeat(8, 1fr)" gap={1.5} mb={3}>
                  {COLOR_PALETTE.map(c => (
                    <GridItem key={c}>
                      <Tooltip label={c} fontSize="10px" hasArrow>
                        <Box
                          w="100%"
                          pt="100%"
                          borderRadius="lg"
                          bg={c}
                          cursor="pointer"
                          position="relative"
                          border="2px solid"
                          borderColor={cor === c ? 'gray.800' : 'transparent'}
                          _hover={{ transform: 'scale(1.15)', zIndex: 1 }}
                          transition="all 0.1s"
                          onClick={() => setCor(c)}
                          boxShadow={cor === c ? '0 0 0 2px white, 0 0 0 4px gray' : 'none'}
                        />
                      </Tooltip>
                    </GridItem>
                  ))}
                  {/* Botao + com color picker nativo */}
                  <GridItem>
                    <Tooltip label="Cor personalizada" fontSize="10px" hasArrow>
                      <Box
                        w="100%"
                        pt="100%"
                        borderRadius="lg"
                        bg={
                          cor && isValidHex(cor) && !COLOR_PALETTE.includes(cor) ? cor : 'gray.100'
                        }
                        cursor="pointer"
                        position="relative"
                        border="2px solid"
                        borderColor={
                          cor && isValidHex(cor) && !COLOR_PALETTE.includes(cor)
                            ? 'gray.800'
                            : 'transparent'
                        }
                        _hover={{ transform: 'scale(1.15)', zIndex: 1 }}
                        transition="all 0.1s"
                        onClick={() => {
                          const picker = document.getElementById('kanban-color-picker');
                          if (picker) picker.click();
                        }}
                      >
                        <Flex
                          position="absolute"
                          top={0}
                          left={0}
                          right={0}
                          bottom={0}
                          align="center"
                          justify="center"
                        >
                          <Text
                            fontSize="14px"
                            fontWeight="700"
                            color={
                              cor && isValidHex(cor) && !COLOR_PALETTE.includes(cor)
                                ? 'white'
                                : 'gray.400'
                            }
                            lineHeight={1}
                          >
                            +
                          </Text>
                        </Flex>
                      </Box>
                    </Tooltip>
                    <input
                      id="kanban-color-picker"
                      type="color"
                      defaultValue={isValidHex(cor) ? cor : '#6366F1'}
                      onChange={e => setCor(e.target.value)}
                      style={{
                        position: 'absolute',
                        width: 0,
                        height: 0,
                        opacity: 0,
                        pointerEvents: 'none',
                      }}
                    />
                  </GridItem>
                </Grid>

                {/* Input hex */}
                <InputGroup size="sm">
                  <InputLeftElement pointerEvents="none" h="full">
                    <Box
                      w="14px"
                      h="14px"
                      borderRadius="sm"
                      bg={isValidHex(cor) ? cor : 'gray.200'}
                      border="1px solid"
                      borderColor="gray.300"
                    />
                  </InputLeftElement>
                  <Input
                    value={cor}
                    onChange={e => handleHexChange(e.target.value)}
                    placeholder="#6366F1"
                    borderRadius="lg"
                    fontSize="13px"
                    fontFamily="mono"
                    maxLength={7}
                    bg="white"
                    _focus={{
                      borderColor: 'blue.400',
                      boxShadow: '0 0 0 3px rgba(66, 153, 225, 0.1)',
                    }}
                    _placeholder={{ color: 'gray.300' }}
                  />
                </InputGroup>
                {cor && !isValidHex(cor) && (
                  <Text fontSize="10px" color="orange.400" mt={1} pl={1}>
                    Formato: #RRGGBB
                  </Text>
                )}
              </FormControl>
            </Box>

            {/* Divider vertical */}
            <Divider orientation="vertical" display={{ base: 'none', md: 'block' }} />
            <Divider display={{ base: 'block', md: 'none' }} />

            {/* Coluna direita: departamentos + finalizacao */}
            <Box flex={1} minW={0}>
              <FormControl mb={project && columns && columns.length > 0 ? 5 : 0}>
                <Flex alignItems="center" justifyContent="space-between" mb={2}>
                  <Flex alignItems="center" gap={1.5}>
                    <Buildings size={14} color="#718096" weight="bold" />
                    <FormLabel
                      fontSize="12px"
                      fontWeight="600"
                      color="gray.500"
                      mb={0}
                      textTransform="uppercase"
                      letterSpacing="0.05em"
                    >
                      Departamentos
                    </FormLabel>
                  </Flex>
                  {selectedDepartamentos.length > 0 && (
                    <Badge colorScheme="blue" borderRadius="full" fontSize="10px" px={2} py={0.5}>
                      {selectedDepartamentos.length}
                    </Badge>
                  )}
                </Flex>
                <Box
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="xl"
                  maxH="220px"
                  overflowY="auto"
                  bg="white"
                  css={{
                    '&::-webkit-scrollbar': { width: '4px' },
                    '&::-webkit-scrollbar-track': { background: 'transparent' },
                    '&::-webkit-scrollbar-thumb': { background: '#CBD5E0', borderRadius: '2px' },
                  }}
                >
                  {departamentos.length === 0 ? (
                    <Flex align="center" justify="center" py={6} color="gray.300">
                      <Text fontSize="13px">Nenhum departamento</Text>
                    </Flex>
                  ) : (
                    <VStack align="stretch" spacing={0} p={1}>
                      {departamentos.map(dep => {
                        const isSelected = selectedDepartamentos.includes(dep.id);
                        return (
                          <Flex
                            key={dep.id}
                            align="center"
                            px={3}
                            py={2}
                            cursor="pointer"
                            bg={isSelected ? 'blue.50' : 'transparent'}
                            borderRadius="lg"
                            _hover={{ bg: isSelected ? 'blue.50' : 'gray.50' }}
                            onClick={() => toggleDepartamento(dep.id)}
                            transition="background 0.1s"
                          >
                            <Checkbox
                              isChecked={isSelected}
                              onChange={() => toggleDepartamento(dep.id)}
                              mr={3}
                              colorScheme="blue"
                              size="sm"
                            />
                            <Text
                              fontSize="13px"
                              fontWeight={isSelected ? '500' : '400'}
                              color={isSelected ? 'blue.700' : 'gray.600'}
                            >
                              {dep.nome}
                            </Text>
                          </Flex>
                        );
                      })}
                    </VStack>
                  )}
                </Box>
                <Text fontSize="11px" color="gray.400" mt={1.5} pl={1}>
                  Quem pode acessar este projeto
                </Text>
              </FormControl>

              {/* Coluna de finalizacao */}
              {project && columns && columns.length > 0 && (
                <FormControl>
                  <Flex alignItems="center" gap={1.5} mb={2}>
                    <Flag size={14} color="#718096" weight="bold" />
                    <FormLabel
                      fontSize="12px"
                      fontWeight="600"
                      color="gray.500"
                      mb={0}
                      textTransform="uppercase"
                      letterSpacing="0.05em"
                    >
                      Coluna de finalizacao
                    </FormLabel>
                  </Flex>
                  <Select
                    value={colunaFinalizadoId || ''}
                    onChange={e =>
                      setColunaFinalizadoId(e.target.value ? Number(e.target.value) : null)
                    }
                    placeholder="Nenhuma"
                    borderRadius="xl"
                    bg="white"
                    border="1px solid"
                    borderColor="gray.200"
                    fontSize="14px"
                    _focus={{
                      borderColor: 'blue.400',
                      boxShadow: '0 0 0 3px rgba(66, 153, 225, 0.1)',
                    }}
                  >
                    {columns.map(col => (
                      <option key={col.id} value={col.id}>
                        {col.titulo}
                      </option>
                    ))}
                  </Select>
                  <Text fontSize="11px" color="gray.400" mt={1.5} pl={1}>
                    Cards aqui nao geram notificacoes
                  </Text>
                </FormControl>
              )}
            </Box>
          </Flex>
        </ModalBody>

        <ModalFooter borderTop="1px solid" borderColor="gray.100" py={4} px={6} bg="gray.50">
          <Flex gap={2} w="100%" justify="flex-end">
            <Button
              variant="ghost"
              onClick={onClose}
              borderRadius="xl"
              fontWeight="500"
              fontSize="13px"
              color="gray.500"
              _hover={{ bg: 'gray.100' }}
            >
              Cancelar
            </Button>
            <Button
              bg="gray.900"
              color="white"
              onClick={handleSave}
              isDisabled={!nome.trim()}
              borderRadius="xl"
              fontWeight="500"
              fontSize="13px"
              px={6}
              _hover={{ bg: 'gray.800' }}
              _active={{ transform: 'scale(0.98)' }}
            >
              {project ? 'Salvar' : 'Criar projeto'}
            </Button>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
